// ============ HUMAN DESIGN — real ephemeris engine ============
// Computes a real Human Design chart from birth date/time/UTC using actual
// planetary ecliptic longitudes (astronomy-engine), the canonical Rave-Mandala
// gate wheel (Gate 41 begins at 2°Aquarius = 302.0°, 64 gates × 5.625°), the 36
// channels, 9 centers, and the standard Type/Authority/Profile/Definition rules.
// Runs fully client-side (no key). Verified gate anchors: Gate 25 @ 28°15′ Pisces,
// Gate 17 @ 3°52′ Aries.

import * as Astronomy from 'astronomy-engine';

// Gate order around the wheel, zodiacally, starting at Gate 41 @ 302.0°.
// Exported because the mandala's four Quarters are slices of this order —
// see `quarterOfGate` in hdDetail.ts.
export const GATE_ORDER = [
  41, 19, 13, 49, 30, 55, 37, 63, 22, 36, 25, 17, 21, 51, 42, 3, 27, 24, 2, 23, 8, 20, 16, 35,
  45, 12, 15, 52, 39, 53, 62, 56, 31, 33, 7, 4, 29, 59, 40, 64, 47, 6, 46, 18, 48, 57, 32, 50,
  28, 44, 1, 43, 14, 34, 9, 5, 26, 11, 10, 58, 38, 54, 61, 60,
];
const WHEEL_START = 302.0;
const GATE_DEG = 360 / 64; // 5.625
const LINE_DEG = GATE_DEG / 6;
// The wheel keeps dividing by six: gate → line → colour → tone (→ base by five).
// Colour and tone are not decoration — they are what the Variables (PHS) read,
// so a chart without them cannot state a Determination or an Environment.
const COLOR_DEG = LINE_DEG / 6;
const TONE_DEG = COLOR_DEG / 6;

export interface GateLine { gate: number; line: number; color: number; tone: number; base: number }

function gateLine(lon: number): GateLine {
  const d = (((lon - WHEEL_START) % 360) + 360) % 360;
  const gi = Math.floor(d / GATE_DEG) % 64;
  const within = d - gi * GATE_DEG;
  const line = Math.min(6, Math.floor(within / LINE_DEG) + 1);
  const inLine = within - (line - 1) * LINE_DEG;
  const color = Math.min(6, Math.floor(inLine / COLOR_DEG) + 1);
  const inColor = inLine - (color - 1) * COLOR_DEG;
  const tone = Math.min(6, Math.floor(inColor / TONE_DEG) + 1);
  const base = Math.min(5, Math.floor((inColor - (tone - 1) * TONE_DEG) / (TONE_DEG / 5)) + 1);
  return { gate: GATE_ORDER[gi], line, color, tone, base };
}

// ---- Centers (app bodygraph ids) → their gates ----
export const CENTER_GATES: Record<string, number[]> = {
  head: [64, 61, 63],
  ajna: [47, 24, 4, 17, 43, 11],
  throat: [62, 23, 56, 35, 12, 45, 33, 8, 31, 20, 16],
  g: [1, 2, 7, 10, 13, 15, 25, 46],
  heart: [21, 40, 26, 51],
  splenic: [48, 57, 44, 50, 32, 28, 18],
  sacral: [5, 14, 29, 59, 9, 3, 42, 27, 34],
  solar: [6, 37, 22, 36, 30, 55, 49],
  root: [58, 38, 54, 53, 60, 52, 19, 39, 41],
};
export const GATE_CENTER: Record<number, string> = {};
for (const [c, gs] of Object.entries(CENTER_GATES)) for (const g of gs) GATE_CENTER[g] = c;

// ---- The 36 channels (gate pairs) ----
export const CHANNELS: [number, number][] = [
  [1, 8], [2, 14], [3, 60], [4, 63], [5, 15], [6, 59], [7, 31], [9, 52], [10, 20], [10, 34],
  [10, 57], [11, 56], [12, 22], [13, 33], [16, 48], [17, 62], [18, 58], [19, 49], [20, 34],
  [20, 57], [21, 45], [23, 43], [24, 61], [25, 51], [26, 44], [27, 50], [28, 38], [29, 46],
  [30, 41], [32, 54], [34, 57], [35, 36], [37, 40], [39, 55], [42, 53], [47, 64],
];

// ---- 13 activations (HD planet order) ----
const BODIES = [
  { k: 'sun', sym: '☉', th: 'ดวงอาทิตย์', en: 'Sun' },
  { k: 'earth', sym: '⊕', th: 'โลก', en: 'Earth' },
  { k: 'nnode', sym: '☊', th: 'นอร์ทโหนด', en: 'North Node' },
  { k: 'snode', sym: '☋', th: 'เซาท์โหนด', en: 'South Node' },
  { k: 'moon', sym: '☽', th: 'ดวงจันทร์', en: 'Moon' },
  { k: 'mercury', sym: '☿', th: 'พุธ', en: 'Mercury' },
  { k: 'venus', sym: '♀', th: 'ศุกร์', en: 'Venus' },
  { k: 'mars', sym: '♂', th: 'อังคาร', en: 'Mars' },
  { k: 'jupiter', sym: '♃', th: 'พฤหัส', en: 'Jupiter' },
  { k: 'saturn', sym: '♄', th: 'เสาร์', en: 'Saturn' },
  { k: 'uranus', sym: '♅', th: 'ยูเรนัส', en: 'Uranus' },
  { k: 'neptune', sym: '♆', th: 'เนปจูน', en: 'Neptune' },
  { k: 'pluto', sym: '♇', th: 'พลูโต', en: 'Pluto' },
];
const PLANET_BODY: Record<string, any> = {
  mercury: Astronomy.Body.Mercury, venus: Astronomy.Body.Venus, mars: Astronomy.Body.Mars,
  jupiter: Astronomy.Body.Jupiter, saturn: Astronomy.Body.Saturn, uranus: Astronomy.Body.Uranus,
  neptune: Astronomy.Body.Neptune, pluto: Astronomy.Body.Pluto,
};

const norm = (x: number) => ((x % 360) + 360) % 360;

function sunLon(date: Date): number { return norm(Astronomy.SunPosition(Astronomy.MakeTime(date)).elon); }
function moonLon(date: Date): number { return norm(Astronomy.EclipticGeoMoon(Astronomy.MakeTime(date)).lon); }
/**
 * TRUE lunar North Node, taken from the Moon's own orbital plane rather than the
 * mean-node polynomial. The two differ by up to ~1.5°, which is nothing at gate
 * scale (5.625°) but enormous at colour scale (0.156°) — and colour is exactly
 * what the Variables read. On the reference chart the mean node produced the
 * wrong Perspective and the wrong Variables code; this reproduces both.
 *
 * The node is where the orbit crosses the ecliptic, so it is perpendicular to
 * the orbital angular momentum h = r × v: the ascending direction is k × h.
 */
function nodeLon(date: Date): number {
  const time = Astronomy.MakeTime(date);
  try {
    const st = (Astronomy as any).GeoMoonState(time);          // EQJ position + velocity
    const rot = (Astronomy as any).Rotation_EQJ_ECT(time);     // → true ecliptic of date
    const pos = Astronomy.RotateVector(rot, new Astronomy.Vector(st.x, st.y, st.z, time));
    const vel = Astronomy.RotateVector(rot, new Astronomy.Vector(st.vx, st.vy, st.vz, time));
    const hx = pos.y * vel.z - pos.z * vel.y;
    const hy = pos.z * vel.x - pos.x * vel.z;
    return norm((Math.atan2(hx, -hy) * 180) / Math.PI);
  } catch {
    // Mean node fallback if this build of astronomy-engine lacks GeoMoonState.
    const T = time.tt / 36525;
    return norm(125.04452 - 1934.136261 * T + 0.0020708 * T * T + (T * T * T) / 450000);
  }
}
function planetLon(k: string, date: Date): number {
  const time = Astronomy.MakeTime(date);
  const vec = Astronomy.GeoVector(PLANET_BODY[k], time, true);
  try {
    const rot = (Astronomy as any).Rotation_EQJ_ECT(time); // true ecliptic of date
    const s = Astronomy.SphereFromVector(Astronomy.RotateVector(rot, vec));
    return norm(s.lon);
  } catch {
    return norm(Astronomy.Ecliptic(vec).elon); // J2000 ecliptic (~0.4° drift) fallback
  }
}

function bodyLon(k: string, date: Date, sun: number): number {
  if (k === 'sun') return sun;
  if (k === 'earth') return norm(sun + 180);
  if (k === 'nnode') return nodeLon(date);
  if (k === 'snode') return norm(nodeLon(date) + 180);
  if (k === 'moon') return moonLon(date);
  return planetLon(k, date);
}

function activations(date: Date) {
  const sun = sunLon(date);
  const set = new Set<number>();
  const list = BODIES.map(b => {
    const lon = bodyLon(b.k, date, sun);
    const gl = gateLine(lon);
    set.add(gl.gate);
    return { ...b, lon, ...gl, g: `${gl.gate}.${gl.line}` };
  });
  return {
    set, list, sunLine: list[0].line, sunGate: list[0].gate, earthGate: list[1].gate,
    sun: list[0], node: list[2], // Sun and North Node carry the Variables
  };
}

// Design = moment the Sun was 88° of solar arc earlier (~88.5 days before birth).
function designTime(birth: Date): Date {
  const target = norm(sunLon(birth) - 88);
  let d = new Date(birth.getTime() - 89 * 86400000);
  for (let i = 0; i < 10; i++) {
    const err = (((target - sunLon(d)) % 360) + 540) % 360 - 180; // signed shortest
    if (Math.abs(err) < 0.0003) break;
    d = new Date(d.getTime() + (err / 0.9856) * 86400000);
  }
  return d;
}

// ---- graph helpers over defined centers ----
function components(defined: Set<string>, edges: [string, string][]): number {
  const parent: Record<string, string> = {};
  defined.forEach(c => (parent[c] = c));
  const find = (x: string): string => (parent[x] === x ? x : (parent[x] = find(parent[x])));
  for (const [a, b] of edges) if (parent[a] && parent[b]) parent[find(a)] = find(b);
  return new Set([...defined].map(find)).size;
}
function reachable(from: string, to: string, adj: Record<string, Set<string>>): boolean {
  if (from === to) return true;
  const seen = new Set([from]); const q = [from];
  while (q.length) {
    const n = q.shift()!;
    for (const m of adj[n] || []) { if (m === to) return true; if (!seen.has(m)) { seen.add(m); q.push(m); } }
  }
  return false;
}

const PROFILE_TH: Record<string, string> = {
  '1': 'ผู้สืบค้น (Investigator)', '2': 'ผู้สันโดษ (Hermit)', '3': 'ผู้ทดลอง (Martyr)',
  '4': 'ผู้เชื่อมโยง (Opportunist)', '5': 'ผู้แก้ปัญหา (Heretic)', '6': 'แบบอย่าง (Role Model)',
};

// ---- The 64 gates by name. A chart that prints "34.2" and stops is a chart
// nobody can read; the name is what makes an activation mean something. ----
export const GATE_TH: Record<number, string> = {
  1: 'การแสดงออกของตัวตน', 2: 'ทิศทางของตัวตน', 3: 'การจัดระเบียบ', 4: 'การหาคำตอบ',
  5: 'จังหวะที่แน่นอน', 6: 'แรงเสียดทาน', 7: 'บทบาทของตัวตน', 8: 'การมีส่วนร่วม',
  9: 'การโฟกัส', 10: 'พฤติกรรมของตัวตน', 11: 'ความคิด', 12: 'ความระมัดระวัง',
  13: 'ผู้รับฟัง', 14: 'ทักษะแห่งพลัง', 15: 'ความสุดขั้ว', 16: 'ทักษะ',
  17: 'ความคิดเห็น', 18: 'การแก้ไข', 19: 'ความต้องการ', 20: 'ปัจจุบันขณะ',
  21: 'นักล่า', 22: 'ความเปิดกว้าง', 23: 'การหลอมรวม', 24: 'การใช้เหตุผล',
  25: 'จิตวิญญาณของตัวตน', 26: 'นักการตลาด', 27: 'การดูแล', 28: 'นักเล่นเกม',
  29: 'การตอบตกลง', 30: 'การรับรู้ความรู้สึก', 31: 'การนำ', 32: 'ความต่อเนื่อง',
  33: 'ความเป็นส่วนตัว', 34: 'พลัง', 35: 'การเปลี่ยนแปลง', 36: 'วิกฤต',
  37: 'มิตรภาพ', 38: 'นักสู้', 39: 'ผู้ยั่วยุ', 40: 'ความโดดเดี่ยว',
  41: 'การหดตัว/จินตนาการ', 42: 'การเติบโต', 43: 'การหยั่งรู้', 44: 'ความตื่นตัว',
  45: 'ผู้รวบรวม', 46: 'การกำหนดตัวตน', 47: 'การตระหนักรู้', 48: 'ความลึกซึ้ง',
  49: 'หลักการ', 50: 'คุณค่า', 51: 'ความตื่นตระหนก', 52: 'ความนิ่ง',
  53: 'การเริ่มต้น', 54: 'ความทะเยอทะยาน', 55: 'จิตวิญญาณ', 56: 'การกระตุ้น',
  57: 'สัญชาตญาณ', 58: 'ความมีชีวิตชีวา', 59: 'เพศสัมพันธ์', 60: 'การยอมรับ',
  61: 'ความลึกลับ', 62: 'รายละเอียด', 63: 'ความสงสัย', 64: 'ความสับสน',
};
export const GATE_EN: Record<number, string> = {
  1: 'Self-Expression', 2: 'Direction of the Self', 3: 'Ordering', 4: 'Formulization',
  5: 'Fixed Rhythms', 6: 'Friction', 7: 'The Role of the Self', 8: 'Contribution',
  9: 'Focus', 10: 'Behaviour of the Self', 11: 'Ideas', 12: 'Caution',
  13: 'The Listener', 14: 'Power Skills', 15: 'Extremes', 16: 'Skills',
  17: 'Opinions', 18: 'Correction', 19: 'Wanting', 20: 'The Now',
  21: 'The Hunter', 22: 'Openness', 23: 'Assimilation', 24: 'Rationalization',
  25: 'Spirit of the Self', 26: 'The Egoist', 27: 'Caring', 28: 'The Game Player',
  29: 'Saying Yes', 30: 'Recognition of Feelings', 31: 'Leading', 32: 'Continuity',
  33: 'Privacy', 34: 'Power', 35: 'Change', 36: 'Crisis',
  37: 'Friendship', 38: 'The Fighter', 39: 'The Provocateur', 40: 'Aloneness',
  41: 'Contraction', 42: 'Growth', 43: 'Insight', 44: 'Alertness',
  45: 'The Gatherer', 46: 'Determination of the Self', 47: 'Realization', 48: 'Depth',
  49: 'Principles', 50: 'Values', 51: 'Shock', 52: 'Stillness',
  53: 'Beginnings', 54: 'Ambition', 55: 'Spirit', 56: 'Stimulation',
  57: 'Intuitive Insight', 58: 'Vitality', 59: 'Sexuality', 60: 'Acceptance',
  61: 'Inner Truth', 62: 'Detail', 63: 'Doubt', 64: 'Confusion',
};

// ---- Variables (PHS) ----
// Each of the six properties is read off the COLOUR or TONE of one specific
// activation — Design Sun, Design Node, Personality Sun, Personality Node.
// Ported from the hd-core engine, where the tables are verified against a
// reference chart (all seven values reproduce exactly).
const DETERMINATION = ['', 'Appetite', 'Taste', 'Thirst', 'Touch', 'Sound', 'Light'];
const COGNITION = ['', 'Smell', 'Taste', 'Outer Vision', 'Inner Vision', 'Feeling', 'Touch'];
const ENVIRONMENT = ['', 'Caves', 'Markets', 'Kitchens', 'Mountains', 'Valleys', 'Shores'];
const MOTIVATION = ['', 'Fear', 'Hope', 'Desire', 'Need', 'Guilt', 'Innocence'];
const SENSE = ['', 'Security', 'Uncertainty', 'Action', 'Meditation', 'Judgment', 'Closure'];
const PERSPECTIVE = ['', 'Survival', 'Possibility', 'Power', 'Wanting', 'Probability', 'Personal'];

export const PHS_TH: Record<string, string> = {
  Appetite: 'ความอยาก', Taste: 'รสชาติ', Thirst: 'ความกระหาย', Touch: 'การสัมผัส', Sound: 'เสียง', Light: 'แสง',
  Smell: 'กลิ่น', 'Outer Vision': 'การมองออกนอก', 'Inner Vision': 'การมองภายใน', Feeling: 'ความรู้สึก',
  Caves: 'ถ้ำ (พื้นที่ปิด สงบ)', Markets: 'ตลาด (ที่มีผู้คน)', Kitchens: 'ครัว (อบอุ่น ใกล้ชิด)',
  Mountains: 'ภูเขา (ที่สูง มองไกล)', Valleys: 'หุบเขา (ที่ลุ่ม ปลอดภัย)', Shores: 'ชายฝั่ง (รอยต่อ)',
  Fear: 'ความกลัว', Hope: 'ความหวัง', Desire: 'ความปรารถนา', Need: 'ความจำเป็น', Guilt: 'ความรู้สึกผิด', Innocence: 'ความบริสุทธิ์',
  Security: 'ความมั่นคง', Uncertainty: 'ความไม่แน่นอน', Action: 'การลงมือ', Meditation: 'การใคร่ครวญ', Judgment: 'การตัดสิน', Closure: 'การปิดจบ',
  Survival: 'การเอาตัวรอด', Possibility: 'ความเป็นไปได้', Power: 'อำนาจ', Wanting: 'ความต้องการ', Probability: 'ความน่าจะเป็น', Personal: 'ส่วนบุคคล',
};

export interface Variables {
  determination: string; cognition: string; environment: string;
  motivation: string; sense: string; perspective: string; code: string;
}

export interface HDResult {
  typeKey: 'generator' | 'mgen' | 'projector' | 'manifestor' | 'reflector';
  authority: string;
  profile: string;
  profileName: string;
  definition: string;
  definedCenters: string[];
  cross: string;
  personality: any[];
  design: any[];
  birthUTC: string;
  designUTC: string;
  /** Every gate switched on by either side, for the bodygraph. */
  activeGates: number[];
  /** Gate pairs where BOTH ends are active — the channels that actually define. */
  definedChannels: [number, number][];
  /** Gates active on one end only: the half-channels that make you seek the other half. */
  hangingGates: number[];
  variables: Variables;
}

export function computeHD(birthUTC: Date): HDResult {
  const pers = activations(birthUTC);
  const dDate = designTime(birthUTC);
  const des = activations(dDate);
  const active = new Set<number>([...pers.set, ...des.set]);

  const defChannels = CHANNELS.filter(([a, b]) => active.has(a) && active.has(b));
  const defined = new Set<string>();
  const edges: [string, string][] = [];
  const adj: Record<string, Set<string>> = {};
  for (const [a, b] of defChannels) {
    const ca = GATE_CENTER[a], cb = GATE_CENTER[b];
    defined.add(ca); defined.add(cb);
    if (ca !== cb) { edges.push([ca, cb]); (adj[ca] ||= new Set()).add(cb); (adj[cb] ||= new Set()).add(ca); }
  }

  const motors = ['sacral', 'heart', 'solar', 'root'];
  const motorToThroat = defined.has('throat') && motors.some(m => defined.has(m) && reachable(m, 'throat', adj));

  let typeKey: HDResult['typeKey'];
  if (defined.size === 0) typeKey = 'reflector';
  else if (defined.has('sacral')) typeKey = motorToThroat ? 'mgen' : 'generator';
  else if (defined.has('throat') && ['heart', 'solar', 'root'].some(m => defined.has(m) && reachable(m, 'throat', adj))) typeKey = 'manifestor';
  else typeKey = 'projector';

  let authority: string;
  if (defined.has('solar')) authority = 'Emotional (Solar Plexus)';
  else if (defined.has('sacral')) authority = 'Sacral';
  else if (defined.has('splenic')) authority = 'Splenic';
  else if (defined.has('heart')) authority = 'Ego (Heart)';
  else if (defined.has('g')) authority = 'Self-Projected (G)';
  else authority = typeKey === 'reflector' ? 'Lunar (รอบจันทรคติ)' : 'Mental (สิ่งแวดล้อม)';

  const profile = `${pers.sunLine}/${des.sunLine}`;
  const profileName = `${PROFILE_TH[String(pers.sunLine)]} / ${PROFILE_TH[String(des.sunLine)]}`;
  const definition = ['No Definition', 'Single Definition', 'Split Definition', 'Triple Split', 'Quadruple Split'][Math.min(4, components(defined, edges))];

  // Incarnation Cross: 4 Sun/Earth gates + angle from profile.
  const angle = ['1/3', '1/4', '2/4', '2/5', '3/5', '3/6'].includes(profile) ? 'Right Angle Cross'
    : profile === '4/1' ? 'Juxtaposition Cross' : 'Left Angle Cross';
  const cross = `${angle} (${pers.sunGate}/${pers.earthGate} | ${des.sunGate}/${des.earthGate})`;

  // Variables read colour/tone off four specific activations.
  const dir = (tone: number) => (tone <= 3 ? 'L' : 'R');
  const variables: Variables = {
    determination: DETERMINATION[des.sun.color],
    cognition: COGNITION[des.sun.tone],
    environment: ENVIRONMENT[des.node.color],
    motivation: MOTIVATION[pers.sun.color],
    sense: SENSE[pers.sun.tone],
    perspective: PERSPECTIVE[pers.node.color],
    code: `P${dir(pers.sun.tone)}${dir(pers.node.tone)}D${dir(des.sun.tone)}${dir(des.node.tone)}`,
  };

  // A gate with only one end lit is a "hanging" gate — the half you carry and
  // keep looking for in other people. Worth showing; it is where a chart gets
  // personal rather than categorical.
  const inChannel = new Set<number>();
  for (const [a, b] of defChannels) { inChannel.add(a); inChannel.add(b); }
  const hangingGates = [...active].filter(g => !inChannel.has(g)).sort((x, y) => x - y);

  return {
    typeKey, authority, profile, profileName, definition,
    definedCenters: [...defined],
    cross,
    personality: pers.list,
    design: des.list,
    birthUTC: birthUTC.toISOString(),
    designUTC: dDate.toISOString(),
    activeGates: [...active].sort((x, y) => x - y),
    definedChannels: defChannels,
    hangingGates,
    variables,
  };
}

/** Build a UTC Date from local date "YYYY-MM-DD", time "HH:MM", and UTC offset hours. */
export function birthToUTC(date: string, time: string, offsetHours: number): Date {
  const [y, m, d] = (date || '1990-01-01').split('-').map(Number);
  const [hh, mm] = (time || '12:00').split(':').map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0) - offsetHours * 3600000);
}
